import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-keys";
import {
  fetchCategories,
  createCategory,
  fetchServicesByCategoryIds,
  fetchServiceDetail,
  createService,
  updateService,
  fetchStaffForService,
} from "@/lib/services-api";
import type { ServiceCategory } from "@/types/services";

// ─── Categories ───────────────────────────────────────────────────────────────

export function useCategories() {
  return useQuery({
    queryKey: queryKeys.services.categories.list(),
    queryFn: fetchCategories,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (formData: FormData) => createCategory(formData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.services.categories.all(),
      });
    },
  });
}

// ─── Services ─────────────────────────────────────────────────────────────────

export function useServicesByCategory(
  categoryIds: string[],
  enabled: boolean = true
) {
  return useQuery({
    queryKey: queryKeys.services.byCategory.list(categoryIds.join(",")),
    queryFn: () => fetchServicesByCategoryIds(categoryIds),
    enabled: enabled,
    staleTime: 2 * 60 * 1000,
  });
}

export function useServiceDetail(serviceId: string, enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.services.detail(serviceId),
    queryFn: () => fetchServiceDetail(serviceId),
    enabled: enabled && !!serviceId,
    staleTime: 2 * 60 * 1000,
  });
}

export function useCreateService() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (formData: FormData) => createService(formData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.services.byCategory.all(),
      });
    },
  });
}

export function useUpdateService() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      serviceId,
      formData,
    }: {
      serviceId: string;
      formData: FormData;
    }) => updateService(serviceId, formData),
    onSuccess: (_, { serviceId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.services.byCategory.all(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.services.detail(serviceId),
      });
    },
  });
}

// ─── Staff ────────────────────────────────────────────────────────────────────

export function useStaffForService(enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.services.staff.list(),
    queryFn: fetchStaffForService,
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function getCategoryIdByName(
  categories: ServiceCategory[],
  name: string
): string {
  return categories.find((c) => c.name === name)?.id ?? "";
}
